// ============================================
// Accordion.js
// GSAP を使ったアコーディオン（開閉アニメーション）
//
// 使用ライブラリ:
//   GSAP : https://gsap.com/
//
// 機能:
//   - <details> / <summary> の標準 HTML を JS でアニメーション制御する
//   - アニメーション中のクリック連打を防止する（is-running フラグ管理）
//   - 複数アコーディオンが独立して動作する
//
// ============================================
// HTML 構造例:
// ============================================
//
// <!-- アコーディオン1つ分 -->
// <!-- .js-accordion が <details> 要素に相当 -->
// <div class="js-accordion">
//
//   <!-- クリックするタイトル部分 -->
//   <div class="js-accordion__ttlWrap">
//     <h3 class="c-accordion__ttl">よくある質問のタイトル</h3>
//     <!-- アイコンなどをここに入れてもよい -->
//   </div>
//
//   <!-- 開閉するコンテンツ部分（初期状態は CSS で height:0; overflow:hidden にする） -->
//   <div class="js-accordion__contents">
//     <div class="c-accordion__body">
//       <p>ここに回答や詳細テキストを入れる。</p>
//     </div>
//   </div>
//
// </div>
//
// <!-- 複数並べる場合はそのまま繰り返す -->
// <div class="js-accordion">...</div>
// <div class="js-accordion">...</div>
//
// CSS の注意点:
//   .js-accordion__contents {
//     overflow: hidden;  /* 必須: height アニメーションを機能させる */
//     height: 0;         /* 初期状態は閉じておく */
//   }
// ============================================

import { gsap } from "gsap";

// ============================================
// 設定値
// ここを変更するだけでアニメーションの見た目を調整できる
// ============================================
const CONFIG = {
  // タイトルとコンテンツの間の余白（px）
  // 開いたとき content の上に付く marginTop の値
  contentMarginTop: 0,

  // アコーディオンを閉じるときのアニメーション時間（秒）
  closeDuration: 0.4,

  // アコーディオンを開くときのアニメーション時間（秒）
  openDuration: 0.4,

  // イージング（https://gsap.com/docs/v3/Eases/ で確認できる）
  closeEase: "power1.inOut",
  openEase: "power2.inOut",

  // 開いた状態を示す属性（<details> の open 属性に相当）
  openAttr: "open",

  // アニメーション中であることを示す data 属性値
  runningValue: "running",
};

// ============================================
// accordionFunc — ページ内の全アコーディオンを初期化する
// ============================================
function accordionFunc() {
  const triggers = document.querySelectorAll(".js-accordion__ttlWrap");
  if (triggers.length === 0) return;

  triggers.forEach((trigger) => {
    // バインド済みチェック（DOMContentLoaded 後に追加された要素の二重登録防止）
    if (trigger.dataset.accordionBound === "1") return;
    trigger.dataset.accordionBound = "1";

    trigger.addEventListener("click", (e) => {
      e.preventDefault(); // <a> タグが使われている場合に備えてデフォルトを止める

      const accordion = trigger.closest(".js-accordion");
      const content = accordion?.querySelector(".js-accordion__contents");

      // 必要な要素が揃っていない、またはアニメーション中の場合はスキップ
      if (!accordion || !content) return;
      if (accordion.dataset.animStatus === CONFIG.runningValue) return;

      // open 属性の有無で開閉を判定する（<details> の open と同じ考え方）
      if (accordion.hasAttribute(CONFIG.openAttr)) {
        closeAccordion(accordion, content);
      } else {
        openAccordion(accordion, content);
      }
    });
  });
}

// アコーディオンを閉じるアニメーション
function closeAccordion(accordion, content) {
  accordion.dataset.animStatus = CONFIG.runningValue; // アニメーション開始フラグ

  gsap.fromTo(
    content,
    {
      // 閉じる前の現在の高さ・透明度・マージンを取得して開始値にする
      height: content.offsetHeight,
      opacity: 1,
      marginTop: CONFIG.contentMarginTop,
    },
    {
      height: 0,
      opacity: 0,
      marginTop: 0,
      duration: CONFIG.closeDuration,
      ease: CONFIG.closeEase,
      onComplete: () => {
        accordion.removeAttribute(CONFIG.openAttr); // 閉じた状態を示す属性を外す
        gsap.set(content, { clearProps: "height" }); // GSAP のインラインスタイルを掃除する
        accordion.dataset.animStatus = ""; // アニメーション完了フラグをリセット
      },
    },
  );
}

// アコーディオンを開くアニメーション
function openAccordion(accordion, content) {
  accordion.setAttribute(CONFIG.openAttr, ""); // 先に open 属性を付ける（DOM に表示させる）
  accordion.dataset.animStatus = CONFIG.runningValue;

  // open にした直後は height が auto になるため、
  // scrollHeight で実際の高さを取得してからアニメーション開始する
  // requestAnimationFrame で 1 フレーム待つことで scrollHeight を正確に取れる
  requestAnimationFrame(() => {
    const targetH = content.scrollHeight;

    gsap.fromTo(
      content,
      {
        height: 0,
        opacity: 0,
        marginTop: 0,
      },
      {
        height: targetH,
        opacity: 1,
        marginTop: CONFIG.contentMarginTop,
        duration: CONFIG.openDuration,
        ease: CONFIG.openEase,
        onComplete: () => {
          // アニメーション完了後は height を auto に戻す
          // （コンテンツが動的に変化しても追従できるようにする）
          gsap.set(content, { height: "auto" });
          accordion.dataset.animStatus = "";
        },
      },
    );
  });
}

export { accordionFunc };
